import { GLOBAL_ROLES, type ITenant, type IUser, ROLES, userProperties } from '@inpart-io/devtools-iap';
import {
    analyticsManager,
    GET_TENANT_CURRENT,
    GET_USER_CURRENT,
    GET_USER_TENANT_ACCESS,
    RETRIEVE_TENANT_CURRENT,
    RETRIEVE_USER_CURRENT,
    RETRIEVE_USER_TENANT_ACCESS,
    SESSION_MODULE
} from '@inpart-io/devtools-vue';
import { type IExtendedUserTenantAccess, UserTenantAccessCommonCalculatedFields } from '@inpart-io/padme-iap';
import { tenantProperties, userTenantAccessProperties } from '@inpart-io/padme-iap';
import { type RouteRecordRaw } from 'vue-router';

import { ROUTE_DATA } from '@/constants/routes.constants';
import { mainRoute } from '@/layouts/main/routes';
import { router } from '@/router';
import { store } from '@/store';
import { trackConfig, trackOtherConfig } from '@/track/config';
import { trackIssuesManager } from '@/track/managers/issues-manager';

import AuthLayout from './auth-layout.vue';

export const authSubRoutes: RouteRecordRaw[] = [mainRoute];

export const authRoute: RouteRecordRaw = {
    path: '/',
    component: AuthLayout,
    children: authSubRoutes,
    beforeEnter: async (to, from, next) => {
        try {
            const userPromise = store.dispatch(`${SESSION_MODULE}/${RETRIEVE_USER_CURRENT}`, { properties: userProperties.CURRENT_USER });
            const tenantPromise = store.dispatch(`${SESSION_MODULE}/${RETRIEVE_TENANT_CURRENT}`, { properties: tenantProperties.DEFAULT });

            await Promise.all([userPromise, tenantPromise]);
            await store.dispatch(`${SESSION_MODULE}/${RETRIEVE_USER_TENANT_ACCESS}`, { properties: userTenantAccessProperties.SESSION });

            const userTenantAccess: IExtendedUserTenantAccess = store.getters[`${SESSION_MODULE}/${GET_USER_TENANT_ACCESS}`];
            const user: IUser = store.getters[`${SESSION_MODULE}/${GET_USER_CURRENT}`];
            const tenant: ITenant = store.getters[`${SESSION_MODULE}/${GET_TENANT_CURRENT}`];

            if (trackConfig.webappBaseUrl?.length > 0) {
                try {
                    trackOtherConfig.tenant = tenant.name;
                    await trackIssuesManager.search();
                    trackOtherConfig.enabled = true;
                } catch (error) {
                    console.error('Track is not available for this tenant: ', error);
                }
            }

            initializeAnalytics(user, tenant, userTenantAccess);

            next();
        } catch (error) {
            next({ name: ROUTE_DATA.NO_ACCESS_PAGE.name, params: { id: error?.status }, replace: true });
        }
    }
};

function initializeAnalytics(user: IUser, tenant: ITenant, userTenantAccess: IExtendedUserTenantAccess): void {
    const userMap = new Map<string, string | boolean>();
    const businessProfile = userTenantAccess[UserTenantAccessCommonCalculatedFields.BusinessProfiles]?.[0];

    // Add user application roles
    userTenantAccess?.applicationRoles?.forEach((roleId) => {
        userMap.set(`role_${ROLES[roleId].toLowerCase()}`, true);
    });

    // Add user global roles
    userTenantAccess?.globalRoles?.forEach((roleId) => {
        userMap.set(`global_${GLOBAL_ROLES[roleId].toLowerCase()}`, true);
    });

    // Add user business profile values
    userMap.set('bp_name', businessProfile?.name ?? 'N/A');
    userMap.set('bp_contract', businessProfile?.contractProfile ?? 'N/A');

    // Initialize analytics manager
    analyticsManager.startUp(
        {
            groupId: tenant.name,
            userId: String(user.dobjId),
            userProperties: Object.fromEntries(userMap)
        },
        router
    );
}
